import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SUPPORTED_LANGS, DEFAULT_LANG, isSupportedLang } from '../constants/languages';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // La lingua "vera" è sempre quella nel primo segmento dell'URL.
  // Se manca o non è valida, usiamo la lingua di default finché il
  // redirect di App.js non sistema l'indirizzo.
  const language = useMemo(() => {
    const segment = location.pathname.split('/')[1];
    return isSupportedLang(segment) ? segment : DEFAULT_LANG;
  }, [location.pathname]);

  // Ricordiamo l'ultima lingua visitata: serve solo per decidere dove
  // mandare chi arriva sulla home nuda "/".
  useEffect(() => {
    try {
      localStorage.setItem('language', language);
    } catch {
      // localStorage non disponibile (es. modalità privata): ignora
    }
  }, [language]);

  // Cambia lingua mantenendo la pagina corrente: sostituisce solo il
  // prefisso nell'URL (es. /en/works/12 -> /it/works/12)
  const toggleLanguage = useCallback(() => {
    const newLanguage = language === 'it' ? 'en' : 'it';
    const segments = location.pathname.split('/');

    if (isSupportedLang(segments[1])) {
      segments[1] = newLanguage;
    } else {
      segments.splice(1, 0, newLanguage);
    }

    const newPath = segments.join('/') || `/${newLanguage}`;
    navigate(`${newPath}${location.search}${location.hash}`);
  }, [language, location, navigate]);

  const t = useCallback(
    (translations) => {
      if (!translations) return '';
      return translations[language] ?? translations[DEFAULT_LANG];
    },
    [language]
  );

  const value = useMemo(
    () => ({ language, lang: language, supportedLangs: SUPPORTED_LANGS, toggleLanguage, t }),
    [language, toggleLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
